import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Analytics, Settings, Article, Logout, Person, Brightness4, Brightness7, AutoFixHigh } from '@mui/icons-material';
import heyDuLogo from '../../images/logo.png';
import User from '../../api/user';
import { useDispatch } from 'react-redux';
import { show } from '../../reducers/notificationSlice';
import { Avatar, Divider } from '@mui/material';
import { useThemeMode } from '../../theme/ThemeProvider';
import {
    clearDashboardAuthSession,
    getDashboardUserId,
    hasDashboardAuthSession,
} from '../../utils/dashboardAuth';

const drawerWidth = 240;

const NAV_ITEMS = [
    {
        icon: <Settings />,
        text: "Manage",
        link: "/dashboard/manage"
    },
    {
        icon: <Article />,
        text: "Manage Report",
        link: "/dashboard/report-data"
    },
    {
        icon: <Analytics />,
        text: "Analytics",
        link: "/dashboard/analytics"
    },
    {
        icon: <AutoFixHigh />,
        text: "CRAP Editor",
        link: "/dashboard/crap-editor"
    }
];

function SidebarProfile() {
    const dispatch = useDispatch();
    const [profile, setProfile] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const userId = getDashboardUserId();
        if (hasDashboardAuthSession() && userId) {
            User.getUserDetails(userId)
                .then(({ data }) => {
                    setProfile(data);
                })
                .catch((err) => dispatch(show({ message: err?.response?.data?.message, open: true, severity: "error" })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogout = () => {
        clearDashboardAuthSession();
        navigate("/login");
    };

    return (
        <Box sx={{
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
        }}>
            {/* Profile Info */}
            <Box
                onClick={() => navigate("/dashboard/profile")}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'translateX(4px)',
                    }
                }}
            >
                {profile?.profileImageURL ? (
                    <Avatar
                        src={profile?.profileImageURL}
                        alt={profile.name}
                        sx={{
                            width: 36,
                            height: 36,
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    />
                ) : (
                    <Avatar
                        sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            border: '2px solid',
                            borderColor: 'primary.light',
                        }}
                    >
                        <Person fontSize="small" />
                    </Avatar>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {profile.name || 'Loading...'}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem'
                        }}
                    >
                        {profile.email || ''}
                    </Typography>
                </Box>
            </Box>

            {/* Logout Button */}
            <ListItemButton
                onClick={handleLogout}
                sx={{
                    mt: 1,
                    py: 0.75,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        transform: 'translateX(4px)',
                        '& .MuiListItemIcon-root': {
                            color: 'error.contrastText',
                        }
                    }
                }}
            >
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500
                    }}
                />
            </ListItemButton>
        </Box>
    );
}

export default function Dashboard(props) {
    const { window } = props;
    const [mobileOpen, setMobileOpen] = useState(false);
    const { mode, toggleTheme } = useThemeMode();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider'
        }}>
            {/* Logo Section */}
            <Box sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <img src={heyDuLogo} alt="HeyDu Logo" style={{ width: '90px', height: 'auto' }} />
            </Box>

            {/* Theme Toggle */}
            <Box sx={{ px: 1.5, pt: 1.5 }}>
                <ListItemButton
                    onClick={toggleTheme}
                    sx={{
                        py: 0.75,
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                            transform: 'translateX(4px)',
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                        primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                        primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: 500
                        }}
                    />
                </ListItemButton>
                <Divider sx={{ mt: 1.5, mb: 1 }} />
            </Box>

            {/* Navigation Items */}
            <List sx={{ flex: 1, pt: 1, px: 1.5 }}>
                {NAV_ITEMS.map((item, idx) => (
                    <NavLink
                        to={item.link}
                        key={idx}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        {({ isActive }) => (
                            <ListItem disablePadding sx={{ mb: 0.75 }}>
                                <ListItemButton
                                    selected={isActive}
                                    sx={{
                                        borderRadius: '8px',
                                        transition: 'all 0.3s ease',
                                        py: 1,
                                        ...(isActive && {
                                            backgroundColor: '#1976d2 !important',
                                            '&:hover': {
                                                backgroundColor: '#1565c0 !important',
                                            },
                                            '& .MuiListItemIcon-root': {
                                                color: '#ffffff !important',
                                            },
                                            '& .MuiListItemIcon-root svg': {
                                                color: '#ffffff !important',
                                                fill: '#ffffff !important',
                                            },
                                            '& .MuiListItemText-primary': {
                                                color: '#ffffff !important',
                                            },
                                            '& .MuiTypography-root': {
                                                color: '#ffffff !important',
                                            },
                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                                        }),
                                        ...(!isActive && {
                                            color: 'text.primary',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                                transform: 'translateX(4px)',
                                            }
                                        })
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Box sx={{
                                            color: isActive ? '#ffffff' : 'inherit',
                                            display: 'flex',
                                            alignItems: 'center',
                                            '& svg': { fontSize: '1.25rem' }
                                        }}>
                                            {item.icon}
                                        </Box>
                                    </ListItemIcon>
                                    <ListItemText>
                                        <Typography
                                            sx={{
                                                fontWeight: isActive ? 600 : 500,
                                                fontSize: '0.875rem',
                                                color: isActive ? '#ffffff !important' : 'inherit'
                                            }}
                                        >
                                            {item.text}
                                        </Typography>
                                    </ListItemText>
                                </ListItemButton>
                            </ListItem>
                        )}
                    </NavLink>
                ))}
            </List>

            {/* Profile Section at Bottom */}
            <SidebarProfile />
        </Box>
    );

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <Box sx={{ display: 'flex', height: '100vh' }}>
            <CssBaseline />

            {/* Mobile Menu Button */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    left: 16,
                    zIndex: 1300,
                    display: { sm: 'none' }
                }}
            >
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                        }
                    }}
                >
                    <MenuIcon />
                </IconButton>
            </Box>

            {/* Sidebar Drawer */}
            <Box
                component="nav"
                sx={{
                    width: { sm: drawerWidth },
                    flexShrink: { sm: 0 }
                }}
            >
                {/* Mobile Drawer */}
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: 'none',
                            boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                {/* Desktop Drawer */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: 'none'
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
                    height: '100vh',
                    overflow: 'auto',
                    backgroundColor: 'background.default',
                    transition: 'all 0.3s ease',
                }}
            >
                <Box sx={{
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    boxSizing: 'border-box'
                }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}
